import { Category, DeducedProduct, Filter, Product, ProductClass, ProductFull } from '../types/catalog';
import { BASE_API_URL, LANG, SITE, TRADEMARKS } from '../config';
import { toCamel } from 'snake-camel';
import fakeApi from './fake-api';

//const isDevMode = process.env.NODE_ENV === 'development';
const isDevMode = false;
export const encodeGet = (params: GetObject) => {
    return Object.entries(params)
        .map(([key, value]) => [key, encodeURIComponent(value)].join('='))
        .join('&');
};

export interface GetObject {
    [key: string]: string;
}

let pendingCounter = 0;
type Subscriber = (k: number) => void;
const subscribers: Subscriber[] = [];
const setPendingCounter = (k: number) => {
    pendingCounter = k;
    for (const sub of subscribers) {
        sub(k);
    }
};
export const counterSubscribe = (sub: Subscriber) => {
    subscribers.push(sub);
};

export const counterUnsubscribe = (sub: Subscriber) => {
    const idx = subscribers.findIndex((s) => s === sub);
    if (idx !== -1) {
        subscribers.splice(idx, 1);
    }
};
const baseHeaders: Record<string, string> = {
    'x-catalog-lang': LANG,
    'x-catalog-tm': TRADEMARKS,
};
if (SITE) {
    baseHeaders['x-catalog-site'] = SITE;
}
const _fetchJson = async (url: string, get: GetObject = {}, params: RequestInit = {}): Promise<any> => {
    setPendingCounter(pendingCounter + 1);
    const fullParams = {
        ...params,
        headers: {
            ...baseHeaders,
            ...params.headers,
        },
    };
    let urlParams = encodeGet(get);
    if (urlParams) {
        urlParams = '?' + urlParams;
    }
    try {
        const res = await fetch(BASE_API_URL + url + urlParams, fullParams);
        const json = await res.json();
        if (!res.ok) {
            throw json;
        }
        setPendingCounter(pendingCounter - 1);
        return toCamel(json);
    } catch (e) {
        setPendingCounter(pendingCounter - 1);
        throw e;
    }
};
const fetchJson = isDevMode ? fakeApi : _fetchJson;

const postJson = (url: string, get: GetObject = {}, body: any, params: RequestInit = {}) => {
    return fetchJson(url, get, {
        mode: 'cors',
        headers: {
            'Content-Type': 'application/json',
            ...params.headers,
        },
        method: 'POST',
        body: JSON.stringify(body),
        ...params,
    });
};

export const fetchCategories = (path: string[] = [], tree = false): Promise<Category[]> => {
    const route = path.length ? path.join('/') + '/' : '';
    const get: GetObject = path.length && tree ? { tree: '1' } : {};
    return fetchJson('/categories/' + route, get).then(({ categories }) => categories);
};

export const fetchNewProducts = (path: string[] = []): Promise<Product[]> => {
    const route = path.length ? path.join('/') + '/' : '';

    return postJson('/products/' + route, {}, { novelty: true, pageSize: 10 }).then(
        ({ products }) => products
    );
};

type TFetchLightingProProducts = (path?: string[]) => Promise<Product[]>;

export const fetchLightingProProducts: TFetchLightingProProducts = async (path = []) => {
    const route = path.length > 0 ? path.join('/') + '/' : '';
    const url = `/products/${route}`;

    const { products } = await postJson(
        url,
        {},
        {
            lightingPro: true,
            pageSize: 10,
        }
    );

    return products;
};

export const fetchPopularProducts = (path: string[] = []): Promise<Product[]> => {
    const route = path.length ? path.join('/') + '/' : '';

    return postJson('/products/' + route, {}, { popular: true, pageSize: 10 }).then(
        ({ products }) => products
    );
};

export const fetchProduct = (url: string): Promise<ProductFull> => {
    return fetchJson('/products' + url).then(({ product }) => product);
};
export const fetchProductByArticle = (article: string): Promise<ProductFull> => {
    return fetchJson('/products/a', { article }).then(({ product }) => product);
};
export const fetchDeduced = (): Promise<DeducedProduct[]> => {
    return fetchJson('/deduced/').then(({ products }) => products);
};

const mapFilter = (f: Filter) => {
    return {
        ...f,
        values: f.values.map((v: any) => ({ value: v.value, productCount: v.count, selected: false })),
    };
};

export const searchProducts = (
    path: string[],
    filters: any
): Promise<{
    products: Product[];
    filters: Filter[];
    classes: ProductClass[];
    total: number;
    archived: Product | null;
}> => {
    const category = path.length ? '/' + path.join('/') + '/' : undefined;
    return postJson('/search', {}, { category, ...filters }).then((result) => ({
        ...result,
        filters: result.filters.map(mapFilter),
    }));
};
